/**
 * Edit a live engagement: who delivers it, what it costs, whether it is still
 * running, and whether cycles generate on their own.
 *
 * Pausing rather than deleting is the intended way to stop one — cycles, work
 * and time are financial history, and the schema refuses to cascade them away.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Engagement } from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

interface UserRow { id: string; firstName: string; lastName: string; role: string; isActive: boolean }

export function EngagementSettingsDialog({ engagement, open, onOpenChange }: {
  engagement: Engagement;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [status, setStatus] = useState(engagement.status);
  const [assignedToId, setAssignedToId] = useState(engagement.assigned_to_id ?? '');
  const [monthlyPrice, setMonthlyPrice] = useState(
    engagement.monthly_price != null ? String(engagement.monthly_price) : ''
  );
  const [autoGenerate, setAutoGenerate] = useState(engagement.auto_generate);
  const [nextCycleDate, setNextCycleDate] = useState(engagement.next_cycle_date ?? '');

  useEffect(() => {
    if (!open) return;
    setStatus(engagement.status);
    setAssignedToId(engagement.assigned_to_id ?? '');
    setMonthlyPrice(engagement.monthly_price != null ? String(engagement.monthly_price) : '');
    setAutoGenerate(engagement.auto_generate);
    setNextCycleDate(engagement.next_cycle_date ?? '');
  }, [open, engagement]);

  const { data: usersData } = useQuery({
    queryKey: ['users', 'for-engagement'],
    queryFn: () => api.listUsers({ limit: 100 }),
    enabled: open,
    retry: false,
  });
  const users = ((usersData?.data ?? []) as UserRow[]).filter((u) => u.isActive);
  const canListUsers = usersData?.success === true;

  const save = useMutation({
    mutationFn: () =>
      api.updateEngagement(engagement.id, {
        status,
        assignedToId: assignedToId || null,
        monthlyPrice: monthlyPrice === '' ? null : Number(monthlyPrice),
        autoGenerate,
        nextCycleDate: nextCycleDate || null,
      }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not save', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Engagement updated' });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Engagement settings</DialogTitle>
          <DialogDescription>{engagement.account_name} · {engagement.service_line_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="ended">Ended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">
                Only an active engagement generates cycles.
              </p>
            </div>
            <div>
              <Label className="text-xs">Monthly price</Label>
              <Input
                className="mt-1" type="number" min={0} step="0.01"
                value={monthlyPrice} onChange={(e) => setMonthlyPrice(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Applies to cycles generated from now on.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs">Assigned to</Label>
            <Select value={assignedToId} onValueChange={setAssignedToId} disabled={!canListUsers}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              New work for roles <em>intern</em> or <em>user</em> is auto-assigned here.
              Existing work keeps its current assignee.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <div>
              <Label className="text-xs">Next cycle date</Label>
              <Input
                className="mt-1" type="date" value={nextCycleDate}
                onChange={(e) => setNextCycleDate(e.target.value)}
              />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer mt-5">
              <input
                type="checkbox" checked={autoGenerate}
                onChange={(e) => setAutoGenerate(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input"
              />
              <span>
                <span className="text-sm">Generate cycles automatically</span>
                <span className="block text-[11px] text-muted-foreground">
                  Requires the scheduler to be enabled server-side.
                </span>
              </span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
